import React, { useState, useEffect, useRef } from "react";
import DynamicField from "./DynamicField";
import "./RightDetails.css";
import api from "../../api";
import SearchSelectPopup from "./SearchSelectPopup";
import AddressContextMenu from "./AddressContextMenu";
import { formatDate } from "../../utils/dateUtils";
import { FiMapPin, FiUsers, FiExternalLink } from "react-icons/fi";

const CompanyDetails = ({ data, onClose, onSave, showToast = (msg, type) => console.log(msg, type) }) => {
  const [formData, setFormData] = useState(data || {});
  const [columns, setColumns] = useState([]);
  const [existingCompanies, setExistingCompanies] = useState([]);
  const [nameWarning, setNameWarning] = useState(false);
  const [pointOfContactDetails, setPointOfContactDetails] = useState(null);
  const [associatedPeople, setAssociatedPeople] = useState([]);
  const [editingPeople, setEditingPeople] = useState(false);
  const [allPeople, setAllPeople] = useState([]);
  const editButtonContainerRef = useRef(null);
  const containerRef = useRef(null);
  const [addressContextMenu, setAddressContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    initialAddress: {},
  });
  const [createdByUser, setCreatedByUser] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [hoveredPersonId, setHoveredPersonId] = useState(null);

  useEffect(() => {
    setFormData(data || {});
    
    // Fetch creator user info if we have created_by
    if (data?.created_by) {
      api.get(`/users/${data.created_by}`)
        .then(response => {
          setCreatedByUser(response.data);
        })
        .catch(err => console.error("Error fetching creator details", err));
    }
  }, [data]);

  useEffect(() => {
    if (formData.point_of_contact) {
      api.get(`/people/${formData.point_of_contact}`)
        .then(response => {
          setPointOfContactDetails(response.data);
        })
        .catch(err => console.error("Error fetching point-of-contact details", err));
    } else {
      setPointOfContactDetails(null);
    }
  }, [formData.point_of_contact]);

  useEffect(() => {
    const fetchColumns = async () => {
      try {
        const response = await api.get("/general/columns/companies");
        const filtered = response.data.filter(
          (col) =>
            !["address1", "address2", "city", "state", "zip", "country", "point_of_contact", 
              "created_at", "updated_at"].includes(col.COLUMN_NAME)
        );
        setColumns(filtered);
      } catch (error) {
        console.error("Error fetching companies columns:", error);
      }
    };
    fetchColumns();
  }, []);

  useEffect(() => {
    const fetchExistingCompanies = async () => {
      try {
        const response = await api.get("/companies");
        setExistingCompanies(response.data);
      } catch (error) {
        console.error("Error fetching companies list:", error);
      }
    };
    fetchExistingCompanies();
  }, []);

  useEffect(() => {
    if (formData.name) {
      const currentName = formData.name.trim().toLowerCase();
      const conflict = existingCompanies.some((company) => {
        if (formData.id && company.id === formData.id) return false;
        return company.name.trim().toLowerCase() === currentName;
      });
      setNameWarning(conflict);
    } else {
      setNameWarning(false);
    }
  }, [formData.name, existingCompanies]);

  useEffect(() => {
    const fetchAllPeople = async () => {
      try {
        const response = await api.get("/people");
        setAllPeople(response.data);
      } catch (error) {
        console.error("Error fetching all people:", error);
      }
    };
    fetchAllPeople();
  }, []);

  useEffect(() => {
    if (data && data.id) {
      const fetchAssociatedPeople = async () => {
        try {
          const response = await api.get(`/companies/${data.id}/people`);
          setAssociatedPeople(response.data);
        } catch (error) {
          console.error("Error fetching associated people:", error);
        }
      };
      fetchAssociatedPeople();
    }
  }, [data]);

  const refreshAssociatedPeople = async () => {
    if (!data?.id) return;
    try {
      const response = await api.get(`/companies/${data.id}/people`);
      setAssociatedPeople(response.data);
    } catch (error) {
      console.error("Error refreshing associated people:", error);
    }
  };

  const handleSetPointOfContact = async (personId) => {
    if (!data?.id) return;
    try {
      await api.put(`/companies/${data.id}`, {
        point_of_contact: personId,
      });
      setFormData((prev) => ({ ...prev, point_of_contact: personId }));
      showToast("Point of contact updated successfully!", "success");
    } catch (error) {
      console.error("Error setting point of contact:", error);
      showToast("Error setting point of contact.", "error");
    }
  };

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleBlur = () => {
    if (onSave) onSave(formData);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSave) onSave(formData);
  };

  const handleAddAsNewCompany = async () => {
    try {
      const postResponse = await api.post("/companies", { name: formData.name });
      showToast(`Company "${formData.name}" created successfully!`, "success");
      const newCompanyId = postResponse.data.id;
      const getResponse = await api.get(`/companies/${newCompanyId}`);
      const newCompany = { ...getResponse.data, fromPage: "Company" };
      if (onSave) onSave(newCompany);
    } catch (error) {
      console.error("Error creating new company:", error);
      showToast("Error creating company", "error");
    }
  };

  const handleSelectPeople = async (selectedPeople) => {
    const removedPeople = associatedPeople.filter(
      (person) => !selectedPeople.some((sel) => sel.id === person.id)
    );
    const addedPeople = selectedPeople.filter(
      (person) => !associatedPeople.some((sel) => sel.id === person.id)
    );
    try {
      const updatePromises = [
        ...addedPeople.map((person) =>
          api.put(`/people/${person.id}`, { company: formData.name })
        ),
        ...removedPeople.map((person) =>
          api.put(`/people/${person.id}`, { company: null })
        ),
      ];
      await Promise.all(updatePromises);
      showToast("People assignments updated!", "success");
      refreshAssociatedPeople();
    } catch (error) {
      console.error("Error updating people assignments:", error);
      showToast("Error updating people.", "error");
    }
  };

  const getCombinedAddress = () => {
    const { address1, address2, city, state, zip, country } = formData;
    const leftParts = [address1, address2, city].filter(Boolean).join(" ");
    const rightParts = [state, zip, country].filter(Boolean).join(" ");
    return leftParts && rightParts ? `${leftParts}, ${rightParts}` : leftParts || rightParts;
  };

  const openAddressMenu = (e) => {
    e.preventDefault();
    const containerRect = containerRef.current.getBoundingClientRect();
    setAddressContextMenu({
      visible: true,
      x: e.clientX - containerRect.left,
      y: e.clientY - containerRect.top,
      initialAddress: {
        address1: formData.address1 || "",
        address2: formData.address2 || "",
        city: formData.city || "",
        state: formData.state || "",
        zip: formData.zip || "",
        country: formData.country || "",
      },
    });
  };

  const handleAddressSave = (newAddress) => {
    setFormData((prev) => ({ ...prev, ...newAddress }));
    if (data && data.id) {
      api.put(`/companies/${data.id}`, newAddress)
        .then(() => showToast("Address updated successfully!", "success"))
        .catch((error) => {
          console.error("Error updating address:", error);
          showToast("Error updating address.", "error");
        });
    }
  };

  const formatLabel = (label) => {
    const replaced = label.replace(/_/g, " ");
    return replaced.charAt(0).toUpperCase() + replaced.slice(1).toLowerCase();
  };

  // Format creator name if available
  const getCreatorName = () => {
    if (createdByUser) {
      return `${createdByUser.first_name || ''} ${createdByUser.last_name || ''}`.trim();
    }
    return formData.created_by || 'Unknown';
  };

  const createdAt = data?.created_at ? formatDate(data.created_at) : null;
  const updatedAt = data?.updated_at ? formatDate(data.updated_at) : null;

  const handleOpenPersonDetails = (person) => {
    if (!person) return;
    
    try {
      // Add a special flag to indicate this is a person details
      const personWrapper = {
        ...person,
        fromPage: "People"  // This is the key flag that determines which details component to use
      };
      
      // Close the current details panel and open person details
      if (onClose) {
        onClose();
        // We need a small delay before opening the new details to allow the current one to close
        setTimeout(() => {
          if (typeof window !== 'undefined') {
            // Dispatch a custom event to open person details - using the same event pattern as in PeopleDetails
            const event = new CustomEvent('openCompanyDetails', { 
              detail: { company: personWrapper }
            });
            window.dispatchEvent(event);
          }
        }, 100);
      }
    } catch (error) {
      console.error("Error opening person details:", error);
      showToast("Error opening person details", "error");
    }
  };

  if (columns.length === 0) {
    return <div>Loading company details...</div>;
  }

  return (
    <div className="right-details-container" ref={containerRef}>
      <div className="right-details-topbar">
        <span className="page-chip">Company</span>
        <button className="close-button" onClick={onClose}>
          ×
        </button>
      </div>
      
      <div className="right-details-header">
        <div
          className="header-top"
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <h2 className="person-name" style={{ margin: 0 }}>
            <input
              type="text"
              value={formData.name || ""}
              onChange={(e) => handleChange("name", e.target.value)}
              onBlur={handleBlur}
              style={{
                fontSize: "1.25rem",
                fontWeight: "bold",
                border: "none",
                outline: "none",
                width: "100%",
              }}
              placeholder="Unnamed"
            />
          </h2>
        </div>
        {nameWarning && (
          <div style={{ color: "orange", fontSize: "12px", marginBottom: "8px" }}>
            This company may already exist.
            <br />
            <button
              type="button"
              onClick={handleAddAsNewCompany}
              style={{
                marginTop: "4px",
                fontSize: "12px",
                padding: "4px 8px",
                cursor: "pointer",
              }}
            >
              Add as new company
            </button>
          </div>
        )}
        <div className="date-info">
          {createdAt && <div className="created-at">Added {createdAt}</div>}
          {updatedAt && <div className="updated-at">Last updated {updatedAt}</div>}
        </div>
      </div>

      <form className="right-details-form" onSubmit={handleSubmit}>
        <div className="fields-section">
          {columns.map((col) => (
            <div key={col.COLUMN_NAME} className="right-details-field">
              <div className="field-label">
                <label htmlFor={col.COLUMN_NAME}>{formatLabel(col.COLUMN_NAME)}</label>
              </div>
              <div className="field-value">
                {col.COLUMN_NAME === "created_by" ? (
                  <span>{getCreatorName()}</span>
                ) : (
                  <DynamicField
                    name={col.COLUMN_NAME}
                    value={formData[col.COLUMN_NAME] || ""}
                    onChange={(value) => handleChange(col.COLUMN_NAME, value)}
                    type={col.DATA_TYPE}
                    onBlur={handleBlur}
                  />
                )}
              </div>
            </div>
          ))}

          <div className="right-details-field">
            <div className="field-label">
              <label><FiMapPin style={{ marginRight: "8px" }} />Address</label>
            </div>
            <div
              className="field-value address-field"
              onClick={openAddressMenu}
              style={{ cursor: "pointer", minHeight: "30px" }}
            >
              {getCombinedAddress() || "Click to add address"}
            </div>
          </div>
        </div>
      </form>

      <div className="company-section">
        <h3>
          <FiUsers style={{ marginRight: "8px" }} />
          People <span style={{ fontSize: "0.9rem", color: "#666" }}>({associatedPeople.length})</span>
        </h3>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
          <button
            onClick={(e) => {
              // Get button position for accurate menu placement
              const buttonRect = e.currentTarget.getBoundingClientRect();
              const containerRect = containerRef.current.getBoundingClientRect();
              setMenuPosition({
                top: buttonRect.bottom - containerRect.top,
                left: buttonRect.left - containerRect.left
              });
              setEditingPeople(true);
            }}
            className="edit-company-button"
            title="Edit People"
            ref={editButtonContainerRef}
          >
            ✏️
          </button>
          {editingPeople && (
            <div style={{ 
              position: "absolute", 
              top: `${menuPosition.top}px`, 
              left: `${menuPosition.left - 240}px`, 
              zIndex: 1000, 
              width: 240 
            }}>
              <SearchSelectPopup
                items={allPeople}
                labelKey="name"
                allowMultiple={true}
                placeholder="Search or add people..."
                initialSelected={associatedPeople}
                useCheckbox={true}
                onSelect={(selectedItems) => handleSelectPeople(selectedItems)}
                onClose={() => setEditingPeople(false)}
                currentCompany={formData.name}
              />
            </div>
          )}
        </div>
        
        {associatedPeople.length === 0 ? (
          <p>No people associated with this company yet.</p>
        ) : (
          <ul className="people-list" style={{ listStyle: "none", padding: 0 }}>
            {associatedPeople.map((person) => (
              <li 
                key={person.id} 
                style={{ 
                  padding: "8px 10px", 
                  borderBottom: "1px solid #eee",
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center",
                  position: "relative"
                }}
                onMouseEnter={() => setHoveredPersonId(person.id)}
                onMouseLeave={() => setHoveredPersonId(null)}
              >
                <span>{person.name || `${person.first_name || ""} ${person.last_name || ""}`}</span>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {hoveredPersonId === person.id && (
                    <button
                      className="view-company-button"
                      onClick={() => handleOpenPersonDetails(person)}
                      title="View Person Details"
                    >
                      <FiExternalLink />
                    </button>
                  )}
                  {formData.point_of_contact === person.id ? (
                    <span
                      style={{
                        backgroundColor: "#4CAF50",
                        color: "white",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontSize: "0.8rem",
                      }}
                    >
                      Point of Contact
                    </span>
                  ) : (
                    <button
                      onClick={() => handleSetPointOfContact(person.id)}
                      style={{
                        border: "none",
                        background: "#eee",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "0.8rem",
                      }}
                    >
                      Set as POC
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {addressContextMenu.visible && (
        <AddressContextMenu
          x={addressContextMenu.x}
          y={addressContextMenu.y}
          initialAddress={addressContextMenu.initialAddress}
          onClose={() => setAddressContextMenu({ ...addressContextMenu, visible: false })}
          onSave={handleAddressSave}
        />
      )}
    </div>
  );
};

export default CompanyDetails;
